import { Button } from "antd";
import { I18n } from "@assets/strings";
import { Range } from "immutable";
import { throttle } from "lodash";
import React, { JSX, useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { ResizePayload, useResizeDetector } from "react-resize-detector";
import { useHistory } from "@core/routing/useHistory";
import { ClipLoader } from "react-spinners";
import { useSwipeable } from "react-swipeable";
import styled, { css } from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { CarouselPostInfos } from "@domain/carousel/dtos";
import { useCurrentProfile, useIsLearner } from "@domain/currentProfile/hooks";
import { Category } from "@domain/lms/category/category";
import { allContentLanguages } from "@domain/lms/contentLanguage";
import { useWindowSize } from "@utils/hooks";
import { theme } from "@ui/styles/theme";
import { mobile } from "@ui/styles/utils";
import { ArrowNavigator } from "@ui/components/arrowNavigator";
import { CategoryEditor } from "@ui/components/category/categoryEditor";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { RadioButton } from "@ui/components/inputs/radio";
import { Selecter } from "@ui/components/inputs/selecter";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { Bloc, row, Row, stack } from "@ui/components/layout";
import { WarningText } from "@ui/components/warningText";
import { Gallery } from "@ui/components/media/gallery";
import { useFitHorizontal } from "./hooks";
import { pdfDocumentOptions } from "./pdfDocumentOptions";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const MAX_CAROUSEL_PAGES_COUNT = 20;
const MIN_CAROUSEL_PAGES_COUNT = 3;
interface PdfViewerProps extends React.HTMLAttributes<HTMLDivElement> {
	source: string;
	pdfHeight: number;
	postCreation?: boolean;
	emitCarouselInfos?: (carouselDetails: CarouselPostInfos | undefined) => void;
	reportPdfWith?: (width: number) => void;
	forcedIndex?: number;
	reportIndex?: ({
		previous,
		current,
		next,
		previousFn,
		nextFn,
	}: {
		previous?: number;
		current?: number;
		next?: number;
		pageHeight: number;
		pageWidth: number;
		previousFn?: () => void;
		nextFn?: () => void;
		fullScreenFn?: unknown;
	}) => void;
	hideArrows?: boolean;
	forcedHeight?: number;
	forcedWidth?: number;
	fullScreen?: boolean;
	fullScreenSetter?: () => void;
	refTitle?: string;
	refId?: string;
	refSlug?: string;
	refCategory?: string;
}
export const PdfViewer: React.FC<PdfViewerProps> = ({
	source,
	pdfHeight,
	postCreation = false,
	emitCarouselInfos,
	reportPdfWith,
	forcedIndex,
	reportIndex,
	hideArrows = false,
	forcedHeight,
	forcedWidth,
	fullScreen,
	fullScreenSetter,
	refTitle,
	refId,
	refSlug,
	refCategory,
	...htmlProps
}) => {
	const currentProfile = useCurrentProfile();
	const isLearner = useIsLearner();
	const [wrapperWidth, setWrapperWidth] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);
	const [wrapperHeight, setWrapperHeight] = useState(0);
	const [pageWidth, setPageWidth] = useState(0);
	const [pageHeight, setPageHeight] = useState(0);
	const [pagesCount, setPagesCount] = useState<number | null>(null);
	const [pageIndex, setPageIndex] = useState(1);
	const [isCarousel, setIsCarousel] = useState<boolean>(false);
	const history = useHistory();
	const [carouselInfos, setCarouselInfos] = useState<CarouselPostInfos>({
		title: "",
		language: currentProfile?.language || "FRENCH",
		category: undefined,
	});
	const { trackingService } = useServices();

	useEffect(() => {
		if (emitCarouselInfos) {
			if (isCarousel) {
				emitCarouselInfos(carouselInfos);
			} else {
				emitCarouselInfos(undefined);
			}
		}
	}, [carouselInfos, isCarousel]);

	useEffect(() => {
		const containerWidth = containerRef.current ? containerRef.current.offsetWidth : 0;
		// TODO Test all Pdf Page Shapes
		if (!!reportPdfWith && containerWidth > 0 && pageWidth) reportPdfWith(pageWidth);
		if (isCarousel) {
			trackingService.track("Create Post - Carousel selected");
		}
	}, [pageWidth]);

	const handlers = useSwipeable({
		onSwipedLeft: () => {
			if (pagesCount && pageIndex < pagesCount) setPageIndex(pageIndex + 1);
		},
		onSwipedRight: () => {
			if (pageIndex > 0) setPageIndex(pageIndex - 1);
		},
	});

	const { width: windowW, height: windowH } = useWindowSize();

	const fitHorizontal = useFitHorizontal(pageWidth, pageHeight, wrapperWidth, pdfHeight);
	const zoomFitHorizontal = useFitHorizontal(pageWidth, pageHeight, windowW, windowH);

	const setWrapperDimensions = useCallback(
		throttle((w, h) => {
			setWrapperWidth(w);
			setWrapperHeight(h);
		}, 500),
		[]
	);

	const { ref } = useResizeDetector({
		onResize: (payload: ResizePayload) => setWrapperDimensions(payload.width, payload.height),
	});

	const [opened, setOpened] = useState(false);

	useEffect(() => {
		if (fullScreen) setOpened(true);
		if (!opened && !!fullScreenSetter) fullScreenSetter();
	}, [fullScreen, opened]);

	useEffect(() => {
		if (pageIndex && pagesCount && !!reportIndex && !forcedIndex) {
			reportIndex({
				previous: pageIndex === 1 ? -1 : pageIndex - 1,
				current: pageIndex,
				next: pageIndex === pagesCount ? -1 : pageIndex + 1,
				pageHeight,
				pageWidth,
				nextFn: pageIndex >= pagesCount ? undefined : () => setPageIndex((i) => i + 1),
				previousFn: pageIndex <= 1 ? undefined : () => setPageIndex((i) => i - 1),
				fullScreenFn: setOpened,
			});
		}
	}, [pageIndex, pagesCount, pageHeight]);

	const carouselHeader = (
		title: string,
		id: string,
		slug: string | undefined,
		category: string | undefined
	): JSX.Element => (
		<>
			<CarouselHeaderContainer>
				<Row style={{ justifyContent: "space-between", marginTop: 0, alignItems: "center" }}>
					<StyledTitleBloc>{title}</StyledTitleBloc>
				</Row>
			</CarouselHeaderContainer>
			<StyledSeeDetailsButton
				onClick={() => {
					history.push(Path.ReferenceCarousel({ id, slug, category: category as Category }));
				}}
			>
				{I18n.t("carousel.detailedView")}
			</StyledSeeDetailsButton>
		</>
	);

	return (
		<Container ref={containerRef} {...htmlProps}>
			<Content ref={ref} isLoading={!pagesCount} pdfHeight={pdfHeight}>
				{forcedIndex === -1 && (
					<Document
						file={source}
						options={pdfDocumentOptions}
						onLoadSuccess={(infos) => setPagesCount(infos.numPages)}
						loading={<ClipLoader />}
					>
						<PageWrapper style={{ opacity: 0.5 }} onClick={() => setOpened(true)}>
							<StyledDumbPage forcedHeight={forcedHeight} forcedWidth={forcedWidth} pdfHeight={pdfHeight} />
						</PageWrapper>
					</Document>
				)}
				{!forcedIndex && (
					<Document
						file={source}
						options={pdfDocumentOptions}
						onLoadSuccess={(infos) => setPagesCount(infos.numPages)}
						loading={<ClipLoader />}
					>
						<PageWrapper onClick={() => setOpened(true)} {...handlers}>
							<Page
								key={pageIndex}
								loading={
									<LoadingWrapper height={wrapperHeight ? Math.min(pdfHeight, wrapperHeight) : pdfHeight}>
										<ClipLoader />
									</LoadingWrapper>
								} // TODO: Replace with skeletons after SEO merge
								pageNumber={pageIndex}
								onLoadSuccess={(page) => {
									setPageWidth(page.width);
									setPageHeight(page.height);
								}}
								renderAnnotationLayer={false}
								renderTextLayer={false}
								width={fitHorizontal ? wrapperWidth : undefined}
								height={!fitHorizontal ? pdfHeight : undefined}
							/>
						</PageWrapper>
						{pagesCount && pageIndex < pagesCount - 1 && (
							<Page
								key={pageIndex + 1}
								pageNumber={pageIndex + 1}
								className="hidden"
								renderAnnotationLayer={false}
								renderTextLayer={false}
							/>
						)}
						{opened && (
							<Gallery
								header={!!refTitle && !!refId ? carouselHeader(refTitle, refId, refSlug, refCategory) : undefined}
								loop={false}
								onClose={() => setOpened(false)}
								items={Range(1, (pagesCount ?? 1) + 1).toArray()}
								initialIndex={pageIndex - 1}
								renderItem={(page) => (
									<Page
										key={page}
										pageNumber={page}
										renderAnnotationLayer={false}
										renderTextLayer={false}
										width={zoomFitHorizontal ? window.innerWidth * 0.9 : undefined}
										height={!zoomFitHorizontal ? window.innerHeight * 0.9 : undefined}
									/>
								)}
							/>
						)}
					</Document>
				)}
				{forcedIndex && forcedIndex !== -1 && !reportIndex && (
					<Document
						file={source}
						options={pdfDocumentOptions}
						onLoadSuccess={(infos) => setPagesCount(infos.numPages)}
						loading={<ClipLoader />}
					>
						<PageWrapper style={{ opacity: 0.5 }} onClick={() => setOpened(true)}>
							<Page
								key={forcedIndex}
								loading={
									<LoadingWrapper height={wrapperHeight ? Math.min(pdfHeight, wrapperHeight) : pdfHeight}>
										<ClipLoader />
									</LoadingWrapper>
								} // TODO: Replace with skeletons after SEO merge
								pageNumber={forcedIndex}
								onLoadSuccess={(page) => {
									setPageWidth(page.width);
									setPageHeight(page.height);
								}}
								renderAnnotationLayer={false}
								renderTextLayer={false}
								width={fitHorizontal ? wrapperWidth : undefined}
								height={!fitHorizontal ? pdfHeight : undefined}
							/>
						</PageWrapper>
					</Document>
				)}
			</Content>

			{pagesCount && !hideArrows && (
				<StyledArrowNavigator
					next={pageIndex >= pagesCount ? undefined : () => setPageIndex((i) => i + 1)}
					previous={pageIndex <= 1 ? undefined : () => setPageIndex((i) => i - 1)}
					small
				>
					<PageCount>
						{pageIndex} / {pagesCount}
					</PageCount>
				</StyledArrowNavigator>
			)}
			{pagesCount && postCreation && !isLearner && (
				<>
					<StyledRadioButton
						id="isCarouselCheckBox"
						checkbox
						disabled={pagesCount > MAX_CAROUSEL_PAGES_COUNT || pagesCount < MIN_CAROUSEL_PAGES_COUNT}
						name="isCarouselCheckBox"
						label={I18n.t(`carousel.post.checkbox.label`)}
						labelStyle={theme.textStyles.carouselLabel}
						checked={isCarousel}
						onChange={() => setIsCarousel(!isCarousel)}
						style={{ marginTop: 14 }}
					/>
					{(pagesCount > MAX_CAROUSEL_PAGES_COUNT || pagesCount < MIN_CAROUSEL_PAGES_COUNT) && (
						<WarningText
							title={I18n.t("carousel.info_warning", {
								min_slide: MIN_CAROUSEL_PAGES_COUNT,
								max_slide: MAX_CAROUSEL_PAGES_COUNT,
							})}
						/>
					)}
				</>
			)}
			{postCreation && isCarousel && (
				<CarouselFormContainer>
					<StyledInfosRow>{I18n.t(`carousel.post.checkbox.labelInfos`)}</StyledInfosRow>
					<StyledTitleRow>
						<StyledSimpleTextInput
							placeholder={I18n.t("carousel.post.title.placeholder")}
							value={carouselInfos.title}
							textLimit={100}
							onChange={(title) =>
								setCarouselInfos({
									...carouselInfos,
									title,
								})
							}
							style={{
								width: "-webkit-fill-available",
							}}
							autoFocus
							height={34}
						/>
					</StyledTitleRow>
					<Row style={{ justifyContent: "space-between" }}>
						<StyledCategoryBloc>
							<CategoryEditor
								inputStyle={{
									minWidth: 200,
									maxWidth: 250,
								}}
								menuHeight={185}
								category={carouselInfos.category}
								setCategory={(category) =>
									category
										? setCarouselInfos({
												...carouselInfos,
												category,
											})
										: undefined
								}
								label={`${I18n.t("group.edition.category")}`}
							/>
						</StyledCategoryBloc>
						<StyledLanguageBloc>
							<Selecter
								{...htmlProps}
								menuHeight={185}
								placeholder={I18n.t("course.editor.language.placeholder")}
								value={carouselInfos.language}
								onChange={(language) =>
									setCarouselInfos({
										...carouselInfos,
										language,
									})
								}
								options={allContentLanguages.map((lang) => ({
									value: lang,
									label: I18n.t(`language.${lang}`),
								}))}
							/>
						</StyledLanguageBloc>
					</Row>
				</CarouselFormContainer>
			)}
		</Container>
	);
};

const Container = styled.div`
	${stack(10, "flex-start", "stretch")};
`;

const CarouselFormContainer = styled.div``;

const StyledInfosRow = styled(Row)`
	font-family: Sora;
	font-style: normal;
	font-weight: 500;
	font-size: 12px;
	line-height: 17px;
	color: #000000;
	margin-bottom: 14px;
`;

const StyledTitleRow = styled(Row)``;

const StyledCategoryBloc = styled(Bloc)``;

const StyledLanguageBloc = styled(Bloc)``;

const StyledSimpleTextInput = styled(SimpleTextInput)``;

const Content = styled.div.withConfig({
	shouldForwardProp: (prop) => !["isLoading", "pdfHeight"].includes(prop),
})<{ isLoading: boolean; pdfHeight: number }>`
	${row(0, "center")};
	flex: 1;
	width: 100%;
	${({ isLoading, pdfHeight }) => isLoading && `height: ${pdfHeight}px`};
	> * {
		${({ isLoading, theme }) => isLoading && `border: solid 1px ${theme.colors.lightergray};`}
		border-radius: 7px;
		overflow: hidden;
	}

	.hidden  {
		display: none;
	}
`;

const StyledDumbPage = styled.div.withConfig({
	shouldForwardProp: (prop) => !["forcedHeight", "forcedWidth", "pdfHeight"].includes(prop),
})<{ forcedHeight?: number; forcedWidth?: number; pdfHeight: number }>`
	min-height: ${({ forcedHeight, pdfHeight }) =>
		forcedHeight && forcedHeight > pdfHeight ? pdfHeight : forcedHeight}px;
	max-height: ${({ forcedHeight, pdfHeight }) =>
		forcedHeight && forcedHeight > pdfHeight ? pdfHeight : forcedHeight}px;
	min-width: ${({ forcedWidth }) => forcedWidth}px;
	max-width: ${({ forcedWidth }) => forcedWidth}px;
	background:
		linear-gradient(263.27deg, rgba(0, 0, 0, 0.34) 20.91%, rgba(0, 0, 0, 0.2754) 65.95%, rgba(0, 0, 0, 0.2108) 118.5%),
		#ffffff;
	box-shadow: 4px 0px 4px rgba(0, 0, 0, 0.25);
	border: 0;
`;

const StyledArrowNavigator = styled(ArrowNavigator)`
	align-self: center;
`;

const PageCount = styled(PrimaryMedium)`
	width: 80px;
	text-align: center;
	flex-shrink: 0;
	flex-grow: 0;
`;

const LoadingWrapper = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "height",
})<{ height: number }>`
	width: 100%;
	height: ${({ height }) => height}px;
`;

const PageWrapper = styled.div`
	cursor: zoom-in;
`;

const CarouselHeaderContainer = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	justify-content: flex-start;
	font-size: 22px;
	@media ${mobile} {
		font-size: 13px;
	}
`;

const StyledTitleBloc = styled.div`
	margin-top: 0;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	//max-width: 70%;
`;

const StyledSeeDetailsButton = styled(Button)`
	position: absolute;
	top: 10px;
	right: 10px;
	width: 164px;
	background: #ffffff;
	border-radius: 8px;
	height: 33px;
	font-style: normal;
	font-weight: 700;
	font-size: 14px;
	color: #000000;
	cursor: pointer;
	padding: 0px 10px;
`;

const StyledRadioButton = styled(RadioButton)<{ disabled: boolean }>`
	${({ disabled }) =>
		disabled &&
		css`
			input[type="checkbox"] {
				background: rgba(0, 0, 0, 0.1);
			}

			label {
				color: rgba(0, 0, 0, 0.4);
			}
		`};
`;
